/**
 * TransientStayMutateDrawer - Sheet/Dialog wrapper for TransientStayForm (drawer variant)
 *
 * Handles: Sheet/Dialog chrome with toggle, data fetching, mutations, discard dialog.
 * Form fields are rendered by TransientStayForm with variant="drawer".
 */

import { useEffect, useState } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { PanelRight, Maximize2 } from 'lucide-react'
import {
  useTransientStayById,
  useCreateTransientStay,
  useUpdateTransientStay,
} from '@/hooks/useTransientStays'
import { TransientStayForm } from './TransientStayForm'
import type { TransientStayFormValues } from './transient-stay-form-schema'
import type { TransientStayFormData } from '@/types/transient-stays'

type DrawerMode = 'sidePanel' | 'modal'

interface TransientStayMutateDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  transientStayId?: string
}

export function TransientStayMutateDrawer({
  open,
  onOpenChange,
  transientStayId,
}: TransientStayMutateDrawerProps) {
  const isEditing = !!transientStayId

  // Data fetching
  const { data: existingStay, isLoading: isLoadingStay } =
    useTransientStayById(transientStayId)

  // Mutations
  const createTransientStay = useCreateTransientStay()
  const updateTransientStay = useUpdateTransientStay()

  // State
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)
  const [formDirty, setFormDirty] = useState(false)
  const [drawerMode, setDrawerMode] = useState<DrawerMode>('sidePanel')

  // Reset when not editing
  useEffect(() => {
    if (!isEditing && open) {
      setFormDirty(false)
    }
  }, [isEditing, open])

  // Handle close with dirty check
  const handleClose = () => {
    if (formDirty) {
      setShowDiscardDialog(true)
    } else {
      onOpenChange(false)
    }
  }

  const handleDiscard = () => {
    setShowDiscardDialog(false)
    setFormDirty(false)
    onOpenChange(false)
  }

  // Form submission handler (receives parsed data from TransientStayForm)
  const handleSubmit = async (values: TransientStayFormValues) => {
    const formData: TransientStayFormData = {
      location_id: values.location_id,
      client_id: values.client_id,
      aircraft_id: values.aircraft_id,
      unit_id: values.unit_id || null,
      check_in: new Date(values.check_in).toISOString(),
      expected_check_out: new Date(values.expected_check_out).toISOString(),
      actual_check_out: values.actual_check_out
        ? new Date(values.actual_check_out).toISOString()
        : null,
      booking_type: values.booking_type,
      status: values.status,
      source: values.source || null,
      rate_type: values.rate_type || null,
      rate_amount: values.rate_amount ?? null,
      discount_amount: values.discount_amount ?? null,
      discount_reason: values.discount_reason || null,
      tax_amount: values.tax_amount ?? null,
      notes: values.notes || null,
      special_requests: values.special_requests || null,
    }

    try {
      if (isEditing && transientStayId) {
        await updateTransientStay.mutateAsync({
          id: transientStayId,
          data: formData,
        })
      } else {
        await createTransientStay.mutateAsync(formData)
      }
      setFormDirty(false)
      onOpenChange(false)
    } catch {
      // Error is handled by mutation hook
    }
  }

  const isSubmitting =
    createTransientStay.isPending || updateTransientStay.isPending
  const isLoading = isLoadingStay && isEditing

  const title = isEditing ? 'Edit Transient Stay' : 'New Booking'
  const description = isEditing
    ? 'Update the transient stay details below.'
    : 'Create a new transient booking.'

  const toggleModeButton = (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 shrink-0"
          onClick={() =>
            setDrawerMode(drawerMode === 'sidePanel' ? 'modal' : 'sidePanel')
          }
        >
          {drawerMode === 'sidePanel' ? (
            <Maximize2 className="w-4 h-4" />
          ) : (
            <PanelRight className="w-4 h-4" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {drawerMode === 'sidePanel' ? 'Switch to modal' : 'Switch to side panel'}
      </TooltipContent>
    </Tooltip>
  )

  const formElement = (
    <TransientStayForm
      variant="drawer"
      stay={isEditing ? existingStay : undefined}
      formId="transient-stay-form"
      isEditing={isEditing}
      isLoading={isLoading}
      disabled={isSubmitting}
      onSubmit={handleSubmit}
    />
  )

  const footerButtons = (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={handleClose}
        disabled={isSubmitting}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        form="transient-stay-form"
        disabled={isSubmitting}
      >
        {isSubmitting
          ? 'Saving...'
          : isEditing
            ? 'Save Changes'
            : 'Create Booking'}
      </Button>
    </>
  )

  const discardDialog = (
    <AlertDialog
      open={showDiscardDialog}
      onOpenChange={setShowDiscardDialog}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Discard changes?</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes. Are you sure you want to close without
            saving?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Continue Editing</AlertDialogCancel>
          <AlertDialogAction onClick={handleDiscard}>
            Discard Changes
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )

  if (drawerMode === 'modal') {
    return (
      <>
        <Dialog open={open} onOpenChange={handleClose}>
          <DialogContent className="flex flex-col max-w-[700px] max-h-[90vh] p-0 gap-0">
            <DialogHeader className="px-6 py-4 border-b shrink-0">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <DialogTitle>{title}</DialogTitle>
                  <DialogDescription>{description}</DialogDescription>
                </div>
                {toggleModeButton}
              </div>
            </DialogHeader>

            <ScrollArea className="flex-1 min-h-0">
              {formElement}
            </ScrollArea>

            <DialogFooter className="flex-row justify-end gap-2 px-6 py-4 border-t bg-background shrink-0">
              {footerButtons}
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {discardDialog}
      </>
    )
  }

  return (
    <>
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent
          side="right"
          className="flex flex-col w-full sm:max-w-2xl p-0 overflow-hidden"
          onInteractOutside={(e) => {
            if (formDirty) e.preventDefault()
          }}
          onEscapeKeyDown={(e) => {
            if (formDirty) {
              e.preventDefault()
              setShowDiscardDialog(true)
            }
          }}
        >
          <SheetHeader className="px-4 py-4 border-b shrink-0">
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <SheetTitle>{title}</SheetTitle>
                <SheetDescription>{description}</SheetDescription>
              </div>
              {toggleModeButton}
            </div>
          </SheetHeader>

          <ScrollArea className="flex-1 min-h-0">
            {formElement}
          </ScrollArea>

          <SheetFooter className="flex-row justify-end gap-2 px-4 py-4 border-t bg-background shrink-0">
            {footerButtons}
          </SheetFooter>
        </SheetContent>
      </Sheet>
      {discardDialog}
    </>
  )
}
